<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório de Estoque</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#financeiro')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <v-row class="mt-3" dense>
      <v-col cols="6" md="3">
        <v-card outlined class="pa-3 kpi"><div class="kpi-t">Itens</div><div class="kpi-v">{{ kpis.itens }}</div></v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="pa-3 kpi"><div class="kpi-t">Valor total em estoque</div><div class="kpi-v">{{ formatarMoedaPtBr(kpis.valorTotal) }}</div></v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="pa-3 kpi"><div class="kpi-t">Itens com reserva</div><div class="kpi-v">{{ kpis.comReserva }}</div></v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="pa-3 kpi"><div class="kpi-t">Itens sem disponível</div><div class="kpi-v">{{ kpis.semDisponivel }}</div></v-card>
      </v-col>
    </v-row>

    <div class="d-flex flex-wrap align-center mt-4 mb-4" style="gap: 12px;">
      <v-text-field v-model="filtros.codigo" label="Código" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.descricao" label="Descrição" solo hide-details clearable class="c-filtro" />
      <v-select v-model="filtros.um" :items="opcoesUm" label="UM" solo hide-details clearable class="c-filtro" />
      <v-select v-model="filtros.local" :items="opcoesLocal" label="Local de estoque" solo hide-details clearable class="c-filtro" />
      <v-select v-model="filtros.situacao" :items="opcoesSituacao" item-text="text" item-value="value" label="Situação" solo hide-details class="c-filtro" />
      <v-checkbox v-model="filtros.ocultarZerados" label="Ocultar saldo zero" hide-details class="mt-0" />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || linhas.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>
        <v-list>
          <v-list-item link @click="gerarExcel"><v-list-item-title>Excel</v-list-item-title></v-list-item>
          <v-list-item link @click="gerarPdf"><v-list-item-title>PDF</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-data-table
      :headers="headers"
      :items="linhas"
      :loading="loading"
      item-key="_id"
      show-expand
      single-expand
      :footer-props="{ 'items-per-page-text': 'Linhas por página:', 'page-text': '{0}-{1} de {2}', 'items-per-page-options': [25, 50, 100, -1] }"
      :items-per-page="50"
      class="elevation-1"
    >
      <template v-slot:item="{ item, expand, isExpanded, headers: hs }">
        <tr :class="classeLinha(item)">
          <td v-for="h in hs" :key="h.value" :class="h.align === 'end' ? 'text-end' : ''">
            <template v-if="h.value === 'data-table-expand'">
              <v-btn icon small @click="expand(!isExpanded)">
                <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
            </template>
            <template v-else-if="['reserva','disponivel','saldo'].includes(h.value)">{{ formatarQtd(item[h.value]) }}</template>
            <template v-else-if="['prUnitReal','valorTotal'].includes(h.value)">{{ formatarMoedaPtBr(item[h.value]) }}</template>
            <template v-else>{{ item[h.value] }}</template>
          </td>
        </tr>
      </template>

      <template v-slot:expanded-item="{ item, headers: hs }">
        <td :colspan="hs.length" class="py-2 grey lighten-4">
          <div v-for="(l, i) in item.locais" :key="i" class="local-linha">
            <b>{{ l.local }}</b>
            — reserva {{ formatarQtd(l.reserva) }} · disponível {{ formatarQtd(l.disponivel) }} · saldo {{ formatarQtd(l.saldo) }} · unit. real {{ formatarMoedaPtBr(l.unitario) }}
          </div>
        </td>
      </template>

      <template v-slot:body.append>
        <tr class="linha-total">
          <td></td>
          <td colspan="3">TOTAL (filtro aplicado)</td>
          <td class="text-end">{{ formatarQtd(totais.reserva) }}</td>
          <td class="text-end">{{ formatarQtd(totais.disponivel) }}</td>
          <td class="text-end">{{ formatarQtd(totais.saldo) }}</td>
          <td></td>
          <td class="text-end">{{ formatarMoedaPtBr(totais.valorTotal) }}</td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import relatorioEstoqueService from "@/views/Relatorios/services/relatorioEstoqueService";
import { exportarPdfTabela, formatarMoedaPtBr, normalizarTexto } from "@/views/Relatorios/base/relatorioBaseUtils";
import relatorioPermissionMixin from "@/mixins/relatorioPermissionMixin";

const formatarQtd = (valor) => {
  const n = Number(valor || 0);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const EPS = 0.00001;

export default {
  name: "RelatorioEstoqueView",
  mixins: [relatorioPermissionMixin],
  data() {
    return {
      relatorioSchemas: ["Estoque", "Stock", "Item"],
      relatorioFiltrarPorOwner: false,
      loading: false,
      dados: [],
      filtros: { codigo: "", descricao: "", um: null, local: null, situacao: "todos", ocultarZerados: false },
      opcoesSituacao: [
        { text: "Todos", value: "todos" },
        { text: "Com saldo", value: "comSaldo" },
        { text: "Com reserva", value: "comReserva" },
        { text: "Sem disponível", value: "semDisponivel" },
        { text: "Zerado", value: "zerado" },
        { text: "Negativo", value: "negativo" }
      ],
      headers: [
        { text: "CODIGO", value: "codigo" },
        { text: "DESCRICAO", value: "descricao" },
        { text: "UM", value: "um" },
        { text: "RESERVA", value: "reserva", align: "end" },
        { text: "DISPONIVEL", value: "disponivel", align: "end" },
        { text: "SALDO", value: "saldo", align: "end" },
        { text: "PR_UNIT_real", value: "prUnitReal", align: "end" },
        { text: "VALORTOTAL", value: "valorTotal", align: "end" },
        { text: "", value: "data-table-expand", sortable: false }
      ]
    };
  },
  computed: {
    opcoesUm() {
      return Array.from(new Set(this.dados.map((d) => d.um).filter((u) => u && u !== "-"))).sort();
    },
    opcoesLocal() {
      const s = new Set();
      this.dados.forEach((d) => d.locais.forEach((l) => s.add(l.local)));
      return Array.from(s).sort();
    },
    linhas() {
      const cod = normalizarTexto(this.filtros.codigo).toLowerCase();
      const desc = normalizarTexto(this.filtros.descricao).toLowerCase();
      const { um, local, situacao, ocultarZerados } = this.filtros;

      return this.dados
        .map((d) => {
          // com filtro de local, recalcula os totais só com aquele local
          if (!local) return d;
          const ls = d.locais.filter((l) => l.local === local);
          if (!ls.length) return null;
          const saldo = ls.reduce((a, l) => a + l.saldo, 0);
          const valorTotal = ls.reduce((a, l) => a + l.saldo * l.unitario, 0);
          return {
            ...d,
            locais: ls,
            reserva: ls.reduce((a, l) => a + l.reserva, 0),
            disponivel: ls.reduce((a, l) => a + l.disponivel, 0),
            saldo,
            valorTotal,
            prUnitReal: saldo > 0 ? valorTotal / saldo : ls.reduce((a, l) => a + l.unitario, 0) / ls.length
          };
        })
        .filter((d) => {
          if (!d) return false;
          if (cod && !normalizarTexto(d.codigo).toLowerCase().includes(cod)) return false;
          if (desc && !normalizarTexto(d.descricao).toLowerCase().includes(desc)) return false;
          if (um && d.um !== um) return false;
          if (ocultarZerados && Math.abs(d.saldo) < EPS) return false;
          if (situacao === "comSaldo" && !(d.saldo > EPS)) return false;
          if (situacao === "comReserva" && !(d.reserva > EPS)) return false;
          if (situacao === "semDisponivel" && !(d.disponivel <= EPS)) return false;
          if (situacao === "zerado" && !(Math.abs(d.saldo) < EPS)) return false;
          if (situacao === "negativo" && !(d.saldo < -EPS)) return false;
          return true;
        });
    },
    totais() {
      return this.linhas.reduce(
        (a, d) => ({
          reserva: a.reserva + d.reserva,
          disponivel: a.disponivel + d.disponivel,
          saldo: a.saldo + d.saldo,
          valorTotal: a.valorTotal + d.valorTotal
        }),
        { reserva: 0, disponivel: 0, saldo: 0, valorTotal: 0 }
      );
    },
    kpis() {
      return {
        itens: this.linhas.length,
        valorTotal: this.totais.valorTotal,
        comReserva: this.linhas.filter((d) => d.reserva > EPS).length,
        semDisponivel: this.linhas.filter((d) => d.saldo > EPS && d.disponivel <= EPS).length
      };
    },
    descricaoFiltros() {
      const f = this.filtros;
      const partes = [];
      if (f.codigo) partes.push(`Código: ${f.codigo}`);
      if (f.descricao) partes.push(`Descrição: ${f.descricao}`);
      if (f.um) partes.push(`UM: ${f.um}`);
      if (f.local) partes.push(`Local: ${f.local}`);
      if (f.situacao && f.situacao !== "todos") {
        const op = this.opcoesSituacao.find((o) => o.value === f.situacao);
        partes.push(`Situação: ${op ? op.text : f.situacao}`);
      }
      if (f.ocultarZerados) partes.push("Saldo zero oculto");
      return partes.length ? partes.join(" | ") : "Sem filtros";
    }
  },
  methods: {
    formatarMoedaPtBr,
    formatarQtd,
    classeLinha(item) {
      if (item.saldo < -EPS) return "linha-negativa";
      if (item.saldo > EPS && item.disponivel <= EPS) return "linha-sem-disponivel";
      return "";
    },
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioEstoqueService.listarEstoqueConsolidado();
        this.dados = Array.isArray(lista) ? lista : [];
      } finally {
        this.loading = false;
      }
    },
    gerarExcel() {
      if (!this.linhas.length) return;
      const principal = this.linhas.map((d) => ({
        CODIGO: d.codigo,
        DESCRICAO: d.descricao,
        UM: d.um,
        RESERVA: d.reserva,
        DISPONIVEL: d.disponivel,
        SALDO: d.saldo,
        PR_UNIT_real: Number(d.prUnitReal.toFixed(4)),
        VALORTOTAL: Number(d.valorTotal.toFixed(2))
      }));
      principal.push({
        CODIGO: "TOTAL",
        DESCRICAO: "",
        UM: "",
        RESERVA: this.totais.reserva,
        DISPONIVEL: this.totais.disponivel,
        SALDO: this.totais.saldo,
        PR_UNIT_real: "",
        VALORTOTAL: Number(this.totais.valorTotal.toFixed(2))
      });

      const porLocal = [];
      this.linhas.forEach((d) => d.locais.forEach((l) => porLocal.push({
        CODIGO: d.codigo,
        DESCRICAO: d.descricao,
        UM: d.um,
        LOCAL: l.local,
        RESERVA: l.reserva,
        DISPONIVEL: l.disponivel,
        SALDO: l.saldo,
        PR_UNIT_real: l.unitario,
        VALORTOTAL: Number((l.saldo * l.unitario).toFixed(2))
      })));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(principal), "Estoque");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(porLocal), "Por local");
      XLSX.writeFile(wb, "Relatório de Estoque.xlsx");
    },
    async gerarPdf() {
      if (!this.linhas.length) return;
      await exportarPdfTabela({
        headers: this.headers.filter((h) => h.value !== "data-table-expand"),
        rows: this.linhas,
        nomeArquivo: "Relatório de Estoque.pdf",
        tituloRelatorio: "Relatório de Estoque",
        subtituloRelatorioLinhas: [this.descricaoFiltros],
        formatadoresPorCampo: {
          reserva: (v) => formatarQtd(v),
          disponivel: (v) => formatarQtd(v),
          saldo: (v) => formatarQtd(v),
          prUnitReal: (v) => formatarMoedaPtBr(v),
          valorTotal: (v) => formatarMoedaPtBr(v)
        },
        resumoFinalLinhas: [
          `Itens: ${this.linhas.length}`,
          `Reserva: ${formatarQtd(this.totais.reserva)} | Disponível: ${formatarQtd(this.totais.disponivel)} | Saldo: ${formatarQtd(this.totais.saldo)}`,
          `Valor total: ${formatarMoedaPtBr(this.totais.valorTotal)}`
        ]
      });
    }
  },
  // A busca inicial é disparada pelo relatorioPermissionMixin assim que o acesso é confirmado.
};
</script>

<style scoped>
.c-filtro { max-width: 220px !important; }
.kpi { border-left: 4px solid #1976d2 !important; }
.kpi-t { font-size: 12px; color: #666; }
.kpi-v { font-size: 20px; font-weight: 600; }
.linha-sem-disponivel { background: #fff3e0; }
.linha-negativa { background: #ffebee; }
.linha-total td { font-weight: 700; background: #e8f0fe; }
.local-linha { font-size: 13px; padding: 2px 0; }
</style>
